import FeatureGates from '@atlaskit/feature-gate-js-client';

import { operationalEvent } from '../events';

/**
 * Gates the operational analytics events that trace the Proforma form
 * serialization flow during Help Center request creation. Used to diagnose an
 * intermittent bug where a request submitted with a Proforma form omits the
 * form data on submission even though a form is rendered.
 */
const PROFORMA_TRACK_PORTAL_CREATE_FORM_SERIALIZATION =
	'proforma_track_portal_create_form_serialisation';

export const isProformaSerializationTraceEnabled = (): boolean =>
	FeatureGates.checkGate(PROFORMA_TRACK_PORTAL_CREATE_FORM_SERIALIZATION);

/**
 * The two Proforma serialization events emitted during request creation.
 * `PreSubmit` records the backend-provided expectation for every submit
 * attempt. `PostSerialization` is emitted only when that expectation is not
 * met immediately before the request is sent.
 */
export const ProformaSerializationTraceStep = {
	PreSubmit: 'preSubmit',
	PostSerialization: 'postSerialization',
} as const;

/**
 * Union of the valid trace step values, derived from
 * {@link ProformaSerializationTraceStep} so the type and the runtime map can
 * never drift apart.
 */
export type ProformaSerializationTraceStep =
	(typeof ProformaSerializationTraceStep)[keyof typeof ProformaSerializationTraceStep];

export interface ProformaSerializationTraceAttributes {
	/** UUID generated once per submit attempt; joins all submit-path events. */
	context_formSubmissionId?: string;
	/** Whether the request type is expected to have a Proforma form. */
	context_hasProformaForm?: boolean;
	context_portalId?: number | string;
	context_requestTypeId?: number | string;
	context_projectId?: number | string;
	/** Whether the Proforma form registered with the request-create parent. */
	registerProformaForm_invoked?: boolean;
	/** Whether the Proforma form completed rendering. */
	renderForm_completed?: boolean;
	/** Whether the registered validate callback was present. */
	validateForm_validateFnPresent?: boolean;
	/** Whether validation reported the Proforma form as valid. */
	validateForm_isProformaFormValid?: boolean;
	/** Whether the registered serialize callback was present. */
	serializeForm_serializeFnPresent?: boolean;
	/** Whether serialize produced a non-empty result. */
	serializeForm_serializedResultPresent?: boolean;
	/** Number of serialized answers produced. */
	serializeForm_serializedAnswerCount?: number;
	/** Number of visible Proforma fields at serialization time. */
	serializeForm_visibleFieldCount?: number;
}

/**
 * Fires a Proforma request-create operational event when the
 * `proforma_track_portal_create_form_serialisation` feature gate is enabled.
 * Events share an action subject and are distinguished by `step`; group by
 * `context_formSubmissionId` to connect the pre-submit event with its post-serialization diagnostic.
 */
export const traceProformaSerialization = (
	step: ProformaSerializationTraceStep,
	attributes: ProformaSerializationTraceAttributes = {},
): void => {
	if (!isProformaSerializationTraceEnabled()) {
		return;
	}

	operationalEvent({
		action: 'traced',
		actionSubject: 'proformaFormSerializationTrace',
		source: 'proforma-serialization-trace',
		attributes: {
			step,
			...attributes,
		},
	});
};
